/*
 * The tablet app. For now: sign in, set your password, then the homes and residents you may see.
 * The medicines round goes on top of this next.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, api } from './api.ts';
import type { Home, User } from './api.ts';
import { Brand, Notices, TopBar } from './chrome.tsx';
import { HomeView } from './home-view.tsx';
import { recall, remember, setSignedIn } from './offline.ts';
import { PharmacyShell } from './pharmacy-shell.tsx';

export function App() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    api
      .get<{ user: User }>('/api/auth/me')
      .then((data) => {
        setUser(data.user);
        void remember('me', data.user);
      })
      .catch(async (err: unknown) => {
        /*
         * No connection is not the same as not signed in. A tablet that wakes up in a corridor with
         * no signal carries on as the person who was using it; the server still checks the login on
         * every record when the signal is back, so this loosens nothing.
         */
        if (err instanceof ApiError && err.status === 0) {
          const saved = await recall<User>('me').catch(() => null);
          setUser(saved?.value ?? null);
          return;
        }
        setUser(null);
      })
      .finally(() => setReady(true));
  }, []);

  /*
   * The outbox sends a record only under the login of the person who made it — and not while that
   * person is still on a one-off password. The server refuses everything until they choose their
   * own, and the outbox treats a refusal as final, so doses recorded offline before a manager reset
   * the password would otherwise be marked refused for good. Held back, they go the moment the new
   * password is set.
   */
  useEffect(() => {
    setSignedIn(user && !user.mustSetPassword ? { id: user.id, name: user.name } : null);
  }, [user]);

  if (!ready) return <Splash>Loading…</Splash>;
  if (!user) return <SignIn onSignedIn={setUser} />;
  if (user.mustSetPassword) return <SetPassword user={user} onDone={setUser} />;
  return <SignedIn user={user} onSignedOut={() => setUser(null)} />;
}

function Splash({ children }: { children: React.ReactNode }) {
  return (
    <main className="splash">
      <Brand />
      <p>{children}</p>
    </main>
  );
}

function SignIn({ onSignedIn }: { onSignedIn: (user: User) => void }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setBusy(true);
      setError('');
      try {
        const data = await api.post<{ user: User }>('/api/auth/login', { email, password });
        onSignedIn(data.user);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Something went wrong.');
        setPassword('');
      } finally {
        setBusy(false);
      }
    },
    [email, password, onSignedIn],
  );

  return (
    <main className="signin">
      <form className="card" onSubmit={submit}>
        <Brand />
        <h1>Sign in</h1>
        <p className="muted">Use your own login. Never sign in as someone else.</p>

        <label htmlFor="email">Email</label>
        <input
          id="email"
          type="email"
          autoComplete="username"
          inputMode="email"
          autoCapitalize="off"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />

        <label htmlFor="password">Password</label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        {error ? <p className="error" role="alert">{error}</p> : null}

        <button type="submit" disabled={busy}>{busy ? 'Signing in…' : 'Sign in'}</button>
        <p className="muted small">Forgotten your password? Ask your manager to set a new one.</p>
      </form>
    </main>
  );
}

function SetPassword({ user, onDone }: { user: User; onDone: (user: User) => void }) {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      if (newPassword !== again) {
        setError('The two new passwords are not the same.');
        return;
      }
      setBusy(true);
      setError('');
      try {
        await api.post('/api/auth/password', { currentPassword, newPassword });
        const data = await api.get<{ user: User }>('/api/auth/me');
        onDone(data.user);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'Something went wrong.');
      } finally {
        setBusy(false);
      }
    },
    [currentPassword, newPassword, again, onDone],
  );

  return (
    <main className="signin">
      <form className="card" onSubmit={submit}>
        <Brand />
        <h1>Choose your own password</h1>
        <p className="muted">
          {user.name}, this login still has the password you were given. Pick your own before you use it.
          Three random words make a good password.
        </p>

        <label htmlFor="current">The password you were given</label>
        <input id="current" type="password" autoComplete="current-password" required
          value={currentPassword} onChange={(e) => setCurrent(e.target.value)} />

        <label htmlFor="new">Your new password</label>
        <input id="new" type="password" autoComplete="new-password" minLength={12} required
          value={newPassword} onChange={(e) => setNew(e.target.value)} />

        <label htmlFor="again">Type it again</label>
        <input id="again" type="password" autoComplete="new-password" minLength={12} required
          value={again} onChange={(e) => setAgain(e.target.value)} />

        {error ? <p className="error" role="alert">{error}</p> : null}
        <div className="row-actions">
          <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save my password'}</button>
          <button
            type="button"
            className="quiet"
            onClick={async () => {
              await api.post('/api/auth/logout').catch(() => {});
              window.location.reload();
            }}
          >
            Not you? Sign out
          </button>
        </div>
      </form>
    </main>
  );
}

function SignedIn({ user, onSignedOut }: { user: User; onSignedOut: () => void }) {
  const signOut = useCallback(async () => {
    await api.post('/api/auth/logout').catch(() => {});
    onSignedOut();
  }, [onSignedOut]);

  /*
   * The pharmacy works across every home, usually from a desk, so it gets its own pages and a
   * sidebar. Care home staff go straight into their home, on a tablet, with nothing in the way.
   */
  if (user.roles.includes('pharmacy')) return <PharmacyShell user={user} onSignOut={signOut} />;
  return <HomeStaffShell user={user} onSignOut={signOut} />;
}

function HomeStaffShell({ user, onSignOut }: { user: User; onSignOut: () => void }) {
  const [homes, setHomes] = useState<Home[] | null>(null);
  const [homeId, setHomeId] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .get<{ homes: Home[] }>('/api/homes')
      .then((data) => {
        setHomes(data.homes);
        if (data.homes.length === 1 && data.homes[0]) setHomeId(data.homes[0].id);
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Could not load the homes.'));
  }, []);

  const home = homes?.find((h) => h.id === homeId) ?? null;

  return (
    <div className="shell">
      <TopBar user={user} onSignOut={onSignOut} />
      <main>
        <Notices user={user} />
        {error ? <p className="error" role="alert">{error}</p> : null}

        {!home ? (
          <section>
            <h1>Choose a home</h1>
            {homes === null ? <p className="muted">Loading…</p> : null}
            {homes?.length === 0 ? <p className="muted">You are not set up for any home yet. Ask the pharmacy.</p> : null}
            <ul className="list">
              {(homes ?? []).map((h) => (
                <li key={h.id}>
                  <button type="button" className="row" onClick={() => setHomeId(h.id)}>
                    <span className="row-main">{h.name}</span>
                    <span className="muted small">
                      {h.supplyMode === 'tray' ? 'Trays' : 'Original packs'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : (
          <HomeView
            key={home.id}
            user={user}
            home={home}
            leave={(homes?.length ?? 0) > 1 ? { label: 'Change home', onLeave: () => setHomeId(null) } : null}
          />
        )}
      </main>
    </div>
  );
}
